import { Apple, MessagesSquare, Sparkles, ShieldCheck } from "lucide-react";
import { Reveal, Chapter, IMAGES } from "@/components/Shared";

const INFO = [
  {
    icon: MessagesSquare,
    title: "Family Communication",
    desc: "We partner with parents and guardians — expect open, honest communication about your child's day, growth, and milestones.",
    testId: "parent-info-communication",
  },
  {
    icon: ShieldCheck,
    title: "Safe & Structured",
    desc: "A secure, supervised setting with consistent daily routines that help children feel comfortable, confident, and cared for.",
    testId: "parent-info-safety",
  },
  {
    icon: Sparkles,
    title: "Clean & Organized",
    desc: "A tidy, well-organized environment where children can focus on playing, learning, and just being kids.",
    testId: "parent-info-clean",
  },
];

const ParentInfo = () => (
  <section id="parent-info" data-testid="parent-info-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Chapter num="05" label="Parent Information" />
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.12] text-deepteal-dark">
          The details parents <em className="italic text-terracotta">ask us first</em>.
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-5 lg:grid-cols-3">
        <Reveal className="lg:row-span-1">
          <article
            data-testid="meals-card"
            className="flex h-full flex-col overflow-hidden rounded-3xl border border-amber-900/10 bg-white/80 shadow-lg shadow-deepteal/8 backdrop-blur"
          >
            <div className="h-48 overflow-hidden">
              <img
                src={IMAGES.meals}
                alt="Diverse children enjoying snacks and drinks together at daycare in Halethorpe Maryland"
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="flex flex-1 flex-col p-7">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-terracotta">
                <Apple className="h-5.5 w-5.5 h-5 w-5" strokeWidth={1.8} />
              </div>
              <h3 className="mt-4 font-serif text-2xl font-medium text-deepteal-dark">Meals & Snacks</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Snacks and milk are provided, so children stay fueled for learning and play
                throughout the day.
              </p>
              <p className="mt-4 text-xs leading-relaxed text-slate-500 border-t border-amber-900/10 pt-4">
                Snacks and milk are provided as applicable. Parents should inform the provider of
                all food allergies, dietary restrictions, and special dietary needs. Meal and snack
                offerings may vary.
              </p>
            </div>
          </article>
        </Reveal>

        {INFO.map((item, i) => (
          <Reveal key={item.title} delay={0.08 * (i + 1)}>
            <article
              data-testid={item.testId}
              className="h-full rounded-3xl border border-amber-900/10 bg-white/80 p-7 backdrop-blur transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-deepteal/12"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-terracotta">
                <item.icon className="h-5 w-5" strokeWidth={1.8} />
              </div>
              <h3 className="mt-4 font-serif text-2xl font-medium text-deepteal-dark">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default ParentInfo;
